import * as React from "react";

import { AssistantErrorContent } from "@/AgentThread/AgentThreadSidebar/AgentThreadByIdView/AgentThreadMessage/AssistantErrorContent";
import { AssistantTextContent } from "@/AgentThread/AgentThreadSidebar/AgentThreadByIdView/AgentThreadMessage/AssistantTextContent";
import { ToolDetailPanelContent } from "@/AgentThread/AgentThreadSidebar/AgentThreadByIdView/AgentThreadMessage/ToolDetailPanel";
import { UserMessage } from "@/AgentThread/AgentThreadSidebar/AgentThreadByIdView/AgentThreadMessage/UserMessage";
import { ReconstructedMessage } from "@/shared/domain/admin";
import {
  type AgentThreadMessage,
  type AgentThreadToolResultContent,
  type AgentThreadToolUseContent,
  AgentThreadRole,
  isAgentThreadErrorContent,
  isAgentThreadTextContent,
  isAgentThreadToolResultContent,
  isAgentThreadToolUseContent,
} from "@/shared/domain/agentThread/AgentThreadRecord";
import { displayToolName } from "@/shared/domain/agentThread/clientTools/ClientTools";

import styles from "./ReconstructedMessageRow.module.css";
import {
  estimateTokens,
  formatTokenDelta,
  formatTokenLabel,
} from "./tokenFormat";

const MS_TO_SECONDS_THRESHOLD = 100;

function formatRuntime(runtimeMs: number): string {
  return runtimeMs < MS_TO_SECONDS_THRESHOLD
    ? `${runtimeMs}ms`
    : `${(runtimeMs / 1000).toFixed(2)}s`;
}

/** A tool_use with no captured result (truncated away) still renders a panel. */
function placeholderResult(
  toolUse: AgentThreadToolUseContent,
): AgentThreadToolResultContent {
  return {
    content_type: "tool_result",
    tool_name: toolUse.tool_name,
    tool_use_id: toolUse.tool_use_id,
    runtime_ms: 0,
    status: "unknown",
  };
}

/** A tool_result whose tool_use was truncated away still renders a panel. */
function placeholderUse(
  toolResult: AgentThreadToolResultContent,
): AgentThreadToolUseContent {
  return {
    content_type: "tool_use",
    tool_name: toolResult.tool_name,
    tool_use_id: toolResult.tool_use_id,
    input: {},
  };
}

/** Find a tool_use_id's result block within a message, or null. */
function findToolResult(
  message: AgentThreadMessage | null | undefined,
  toolUseId: string,
): AgentThreadToolResultContent | null {
  for (const block of message?.content ?? []) {
    if (
      isAgentThreadToolResultContent(block) &&
      block.tool_use_id === toolUseId
    ) {
      return block;
    }
  }
  return null;
}

/**
 * Per-text-block display state: its token label, and whether it was
 * compressed. A compressed block shows `~4.5k -> 2.3k tok` (original → sent)
 * and is highlighted individually; an uncompressed one shows a single
 * `~x.yk tok`. The before/after is only used when the compressed and original
 * messages still line up block for block — message-tier compression can
 * restructure text runs.
 */
function describeTextBlock(
  entry: ReconstructedMessage,
  index: number,
  displayedText: string,
): { label: string | null; compressed: boolean } {
  const original = entry.original_message;
  if (
    original !== null &&
    entry.message.content.length === original.content.length
  ) {
    const sent = entry.message.content[index];
    const orig = original.content[index];
    if (
      sent !== undefined &&
      orig !== undefined &&
      isAgentThreadTextContent(sent) &&
      isAgentThreadTextContent(orig)
    ) {
      return {
        label: formatTokenDelta(
          estimateTokens(orig.text),
          estimateTokens(sent.text),
        ),
        compressed: sent.text !== orig.text,
      };
    }
  }
  return {
    label: formatTokenLabel(estimateTokens(displayedText)),
    compressed: false,
  };
}

/**
 * Collapsed "Used Tool X (0.92s · ~1.2k tok)" row; expands to the chat's tool
 * panel. When the tool's *result* was compressed, the summary carries a
 * `compressed` badge and the body offers an in-place toggle to the original —
 * the result lives on its own (skipped) message, so this is where its
 * compression has to surface.
 */
function ToolBlock({
  toolUse,
  resultEntry,
}: {
  toolUse: AgentThreadToolUseContent;
  /** The reconstructed message holding this tool's result, if captured. */
  resultEntry: ReconstructedMessage | null;
}) {
  const [showOriginal, setShowOriginal] = React.useState(false);

  const sentResult = findToolResult(resultEntry?.message, toolUse.tool_use_id);
  const originalResult = findToolResult(
    resultEntry?.original_message,
    toolUse.tool_use_id,
  );
  // An original result exists only when the result message was compressed.
  const compressedTier =
    resultEntry !== null && originalResult !== null ? resultEntry.tier : null;
  const result =
    (showOriginal && originalResult !== null ? originalResult : sentResult) ??
    placeholderResult(toolUse);

  // When the result was compressed, show original -> sent; else one count.
  const tokenLabel =
    compressedTier !== null && originalResult !== null && sentResult !== null
      ? formatTokenDelta(
          estimateTokens(toolUse) + estimateTokens(originalResult),
          estimateTokens(toolUse) + estimateTokens(sentResult),
        )
      : formatTokenLabel(estimateTokens(toolUse) + estimateTokens(result));

  return (
    <details
      className={styles.toolDetails}
      data-tier={compressedTier !== null ? "compressed" : "original"}
    >
      <summary className={styles.toolSummary}>
        Used Tool <b>{displayToolName(toolUse.tool_name)}</b> (
        {formatRuntime(result.runtime_ms)}
        {tokenLabel === null ? "" : ` · ${tokenLabel}`})
      </summary>
      {compressedTier !== null && (
        <div className={styles.toolToggleRow}>
          <button
            type="button"
            className={styles.toggle}
            onClick={() => {
              setShowOriginal((previous) => !previous);
            }}
          >
            {showOriginal ? "Show compressed result" : "Show original result"}
          </button>
        </div>
      )}
      <ToolDetailPanelContent toolUse={toolUse} toolResult={result} />
    </details>
  );
}

interface ReconstructedMessageRowProps {
  entry: ReconstructedMessage;
  /** The reconstructed message holding each tool result, keyed by tool_use_id —
   *  lets a tool_use render request + response (and the result's compression)
   *  together in one panel. */
  resultEntryByToolUseId: Map<string, ReconstructedMessage>;
  /** tool_use_ids present in the turn — a tool_result with a match is skipped
   *  (already shown beside its tool_use). */
  pairedToolUseIds: Set<string>;
}

/**
 * One message of the reconstructed Bedrock prompt. Text and errors render
 * through the shared chat content components; tool requests and results
 * render through the chat's own ``ToolDetailPanelContent``, collapsed behind
 * a "Used Tool" summary. Compression is shown per block — a compressed text
 * block or tool carries its own edge stripe and before/after token count —
 * rather than as one message-level bar, which mislabels tool-only turns.
 */
export function ReconstructedMessageRow({
  entry,
  resultEntryByToolUseId,
  pairedToolUseIds,
}: ReconstructedMessageRowProps) {
  const [showOriginal, setShowOriginal] = React.useState(false);
  const isCompressed = entry.original_message !== null;
  const displayed =
    showOriginal && entry.original_message !== null
      ? entry.original_message
      : entry.message;

  const renderBlock = (
    block: AgentThreadMessage["content"][number],
    role: AgentThreadRole,
    key: number,
  ): React.ReactNode => {
    if (isAgentThreadTextContent(block)) {
      const isUser = role === AgentThreadRole.USER;
      const { label, compressed } = describeTextBlock(entry, key, block.text);
      return (
        // The token label sits directly above its block, aligned to the same
        // side as the bubble. A compressed block carries its own edge stripe
        // — per-block, since one message can mix compressed and verbatim text.
        <div
          key={key}
          className={styles.textBlock}
          data-role={isUser ? "user" : "assistant"}
          data-compressed={compressed ? "true" : undefined}
        >
          {label !== null && (
            <span className={styles.blockTokens}>{label}</span>
          )}
          {isUser ? (
            <UserMessage text={block.text} isFailed={false} />
          ) : (
            <AssistantTextContent
              text={block.text}
              isGenerating={false}
              isCancelling={false}
            />
          )}
        </div>
      );
    }
    if (isAgentThreadToolUseContent(block)) {
      return (
        <ToolBlock
          key={key}
          toolUse={block}
          resultEntry={resultEntryByToolUseId.get(block.tool_use_id) ?? null}
        />
      );
    }
    if (isAgentThreadToolResultContent(block)) {
      // Already shown beside its tool_use; render only orphaned results.
      if (pairedToolUseIds.has(block.tool_use_id)) {
        return null;
      }
      return (
        <ToolBlock
          key={key}
          toolUse={placeholderUse(block)}
          resultEntry={entry}
        />
      );
    }
    if (isAgentThreadErrorContent(block)) {
      return <AssistantErrorContent key={key} error={block} />;
    }
    return null;
  };

  const blocks = displayed.content.map((block, index) =>
    renderBlock(block, displayed.role, index),
  );

  // A roboto message whose only block is a tool_result shown elsewhere has
  // nothing left to render — drop the row rather than leave an empty article.
  if (blocks.every((block) => block === null)) {
    return null;
  }

  return (
    <article className={styles.row}>
      {isCompressed && (
        <div className={styles.badgeRow}>
          <button
            type="button"
            className={styles.toggle}
            onClick={() => {
              setShowOriginal((previous) => !previous);
            }}
          >
            {showOriginal ? "Show compressed" : "Show original"}
          </button>
        </div>
      )}
      {entry.tier === "synthetic" && (
        <div className={styles.badgeRow}>
          <span className={styles.syntheticBadge}>injected</span>
        </div>
      )}
      {blocks}
    </article>
  );
}
